import {
  Box,
  Button,
  Chip,
  Dialog as MuiDialog,
  DialogContent as MuiDialogContent,
  DialogTitle as MuiDialogTitle,
  FormControlLabel as MuiFormControlLabel,
  IconButton,
  Select as MuiSelect,
  Switch as MuiSwitch,
} from "@mui/material";
import type { Theme } from "@mui/material/styles";
import { styled } from "styled-components";

import FormTextFieldOrig from "@/components/UI/FormTextField";
import { ArrowDownIcon } from "@/icons";

export const Dialog = styled(MuiDialog)({
  "& > div > .MuiPaper-root": {
    backgroundColor: "rgba(70,70,70) !important",
    padding: "16px",
    borderRadius: "20px",
    gap: "16px",
  },
  "& .MuiDialogActions-root": {
    margin: "0 auto",
    padding: "16px",
    justifyContent: "center",
    width: "100%",
    "@media (max-width:576px)": {
      bottom: 0,
      position: "absolute",
    },
    backgroundColor: "#000",
  },
});

export const DialogTitleContainer = styled("div")({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  gap: "16px",
});

export const CloseIconButton = styled(IconButton)<{ $theme: Theme }>(({ $theme }) => ({
  backgroundColor: $theme.palette.semanticSurface.dark,
  height: "32px",
  width: "32px",
}));

export const DialogOuterContainer = styled("div")({
  width: "100%",
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "space-between",
});

export const DialogTitle = styled(MuiDialogTitle)({
  display: "flex",
  flexDirection: "column",
  justifyContent: "flex-start",
  alignItems: "flex-start",
  gap: "8px",
  color: "#fff",
  backgroundColor: "#000",
  padding: "16px",
});

export const DialogTitleText = styled("span")({
  color: "#fff",
  fontSize: "24px",
  fontWeight: "700",
});

export const DialogTitleChip = styled(Chip)<{ $theme: Theme }>(({ $theme }) => ({
  backgroundColor: $theme.palette.semanticNeutral.dark,
  fontSize: "16px",
  color: "#000",
  height: "32px",
  width: "152px",

  "& .MuiChip-icon": {
    fontSize: "16px",
    marginLeft: "8px",
    marginRight: "4px",
  },
}));

export const DialogTitleStatus = styled("span")({
  color: "rgba(255,255,255,0.8)",
  fontSize: "16px",
  fontWeight: "400",
  textTransform: "capitalize",
});

export const DialogContent = styled(MuiDialogContent)({
  position: "relative",
  height: "100%",
  display: "flex",
  justifyContent: "center",
  gap: "16px",
  backgroundColor: "rgba(70,70,70)",
  padding: 0,
});

export const FormSection = styled(Box)<{ $matchDbcSize: boolean; $dbcSectionHeight: number }>(
  ({ $matchDbcSize, $dbcSectionHeight }) => ({
    display: "flex",
    flexDirection: "column" as const,
    height: `${$matchDbcSize ? $dbcSectionHeight : "320"}px`,
    width: $matchDbcSize ? "450px" : "766px",
    backgroundColor: "#000",
    alignItems: "flex-start",
    justifyContent: "flex-start",
    gap: "16px",
    padding: "0 16px",
    "@media (max-width:576px)": {
      maxWidth: "350px",
      width: "100%",
    },
  }),
);

export const DBCSection = styled(Box)({
  margin: "auto",
  height: "100%",
  width: "300px",
  backgroundColor: "#000",
  alignItems: "center",
  paddingBottom: "16px",
  "@media (max-width:576px)": {
    maxWidth: "350px",
    width: "100%",
  },
  "& .MuiBox-root": {
    marginRight: 0,
  },
});

export const DialogButton = styled(Button)<{
  $theme: Theme;
}>(({ $theme }) => ({
  bottom: 0,
  right: 0,
  padding: "8px 12px",
  margin: "0 24px",
  backgroundColor: $theme.palette.semanticSurface.dark,
  color: "#A2A2A2",
  textAlign: "center" as const,
  width: "100%",
  fontWeight: "bold",
  fontSize: "16px",
  borderRadius: 32,
  transition: "background-color 0.3s ease",
  "@media (max-width:576px)": {
    width: "100%",
  },
  "&:hover": {
    backgroundColor: $theme.palette.semanticNeutral.main,
  },
  "&.firstButton": {
    marginLeft: "8px",
  },
  "&.submitButton": {
    marginRight: "8px",
    backgroundColor: "#F50561",
    color: "#fff !important",
    "&:hover": {
      backgroundColor: $theme.palette.semanticPrimary.main,
    },
    "&.Mui-disabled": {
      backgroundColor: `${$theme.palette.semanticDisabledBackground.dark} !important`,
      color: `${$theme.palette.semanticDisabledForeground.dark} !important`,
    },
  },
}));

export const FormTextField = styled(FormTextFieldOrig)<{
  $theme: Theme;
}>(({ $theme }) => ({
  borderTopLeftRadius: 8,
  borderTopRightRadius: 8,

  "& label": {
    color: $theme.palette.semanticNeutral.light,

    "&.Mui-focused": {
      color: `${$theme.palette.semanticNeutral.light} !important`,
    },
    "&.Mui-disabled": {
      color: `${$theme.palette.semanticDisabledForeground.dark} !important`,
    },
  },
  "& .MuiInputBase-root": {
    backgroundColor: $theme.palette.semanticSurface.dark,
    color: "#fff",
    borderBottom: `1px ${$theme.palette.semanticNeutral.light} solid`,

    "&.Mui-disabled": {
      borderBottom: `1px ${$theme.palette.semanticDisabledForeground.dark} solid`,
    },
    "&.Mui-focused": {
      borderBottom: `1px ${$theme.palette.semanticSelected.main} solid`,
    },
  },
  "& .MuiInputBase-input.Mui-disabled": {
    WebkitTextFillColor: `${$theme.palette.semanticDisabledForeground.dark} !important`,
  },
  "& .MuiInputBase-input": {
    paddingTop: "24px",
  },
  "& .MuiInputBase-inputMultiline": {
    paddingTop: "4px",
  },
  "& .MuiInputBase-root::before": {
    border: "none !important",
  },
  "& .MuiInputBase-root::after": {
    border: "none",
  },
}));

export const FormControlLabel = styled(MuiFormControlLabel)<{ $theme: Theme }>(({ $theme }) => ({
  "& .MuiFormControlLabel-root": {
    marginLeft: 0,
  },
  "& .MuiTypography-body1": {
    color: "#fff",
    fontSize: "16px",
    marginLeft: "8px",

    "&.Mui-disabled": {
      color: `${$theme.palette.semanticDisabledForeground.dark} !important`,
    },
  },
}));

export const FormSwitch = styled(MuiSwitch)<{ $theme: Theme }>(({ $theme }) => ({
  width: 40,
  height: 22,
  padding: 0,
  marginLeft: "11px",
  "& .MuiSwitch-switchBase": {
    padding: 0,
    margin: 2,
    transitionDuration: "300ms",
    "&.Mui-checked": {
      transform: "translateX(18px)",
      "& .MuiSwitch-thumb": {
        color: $theme.palette.semanticText.light, // Change thumb color when checked
      },

      "& + .MuiSwitch-track": {
        backgroundColor: $theme.palette.semanticSelected.main,
        opacity: 1,
        border: 0,
      },
    },
  },
  "& .MuiSwitch-thumb": {
    boxSizing: "border-box",
    width: 18,
    height: 18,
    color: $theme.palette.semanticNeutral.light,
  },
  "& .MuiSwitch-track": {
    borderRadius: "100px",
    backgroundColor: $theme.palette.semanticSurface.dark,
    opacity: 1,
    border: `1px solid ${$theme.palette.semanticNeutral.light}`,
  },
}));

export const FormSelectField = styled(MuiSelect)<{
  $theme: Theme;
}>(({ $theme }) => ({
  backgroundColor: $theme.palette.semanticSurface.dark,
  borderRadius: 6,
  color: "#fff",
  fontSize: "14px",
  height: "40px",
  marginLeft: "-4px",
  paddingBottom: "10px",

  "&.Mui-focused": {
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: $theme.palette.semanticSelected.main,
    },
  },
  "& label": {
    color: $theme.palette.semanticNeutral.light,
  },
  "& label.Mui-focused": {
    color: `${$theme.palette.semanticSurface.light} !important`,
  },
  "& .MuiFormControl-root": {
    margin: "0",
  },
  "& .MuiInputBase-root": {
    backgroundColor: "transparent",
    color: "#fff",

    "& .Mui-focused": {
      color: `${$theme.palette.semanticSurface.light} !important`,
    },
  },
  "& .MuiInputBase-input": {
    backgroundColor: "transparent",
    paddingTop: "24px",
    "&.Mui-disabled": {
      WebkitTextFillColor: `${$theme.palette.semanticDisabledForeground.dark} !important`,
    },
  },
  "& .MuiInputBase-inputMultiline": {
    paddingTop: "4px",
  },
}));

export const ArrowDown = styled(ArrowDownIcon)({
  right: "24px",
  width: "16px",
});
